import { pxToRem } from '@back-office/core';
import {
  Child,
  qrCodeTypesLabelMap,
  TransactionList,
  TransactionListPagination,
} from '@back-office/http';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Pagination from '@material-ui/lab/Pagination';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyIcon } from '../../icons';
import { getDatetimeText } from '../../utils';
import GcpBox from '../gcp-box';
import GcpCurrencyFormat from '../gcp-currency-format';
import GcpStatus, { TransactionStatus } from '../gcp-status';
import GcpTypography from '../gcp-typography';
import tableHeader from './content';

const useStyles = makeStyles(
  (theme) => ({
    headerRow: {
      verticalAlign: 'bottom',
    },
    headerCell: {
      padding: theme.spacing(3, 0, 1, 0),
      border: '1px solid rgba(172, 181, 189, 0.5)',
      backgroundColor: theme.palette.common.alabaster,
    },
    bodyRow: {
      verticalAlign: 'top',
      cursor: 'pointer',
    },
    bodyCell: {
      border: '1px solid rgba(172, 181, 189, 0.5)',
    },
    avatar: {
      width: pxToRem(14),
      height: pxToRem(14),
    },
    alert: {
      background: '#FFF1F0',
      border: '1px solid #FFA39E',
      boxSizing: 'border-box',
      borderRadius: '2px',
    },
    empty: {
      width: '10rem',
      height: '10rem',
      fill: 'none',
    },
  }),
  { name: 'TransactionList' }
);

interface GcpTransactionListTableProps {
  data: TransactionList[];
  pagination: TransactionListPagination;
  handleOnChangePage: (newPage: number) => void;
  page: number;
  handleOnClickRow: (transaction: TransactionList) => void;
}

const GcpTransactionListTable: FC<GcpTransactionListTableProps> = observer(
  (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { data, pagination, page, handleOnChangePage, handleOnClickRow } =
      props;

    const getChildLabel = (
      childType: Child['type'],
      childStatus: Child['status']
    ): string => {
      if (childType === 'void' && childStatus === 'cleared') {
        return 'Voided';
      }
      if (childType === 'refund' && childStatus === 'cleared') {
        return 'Refunded';
      }
      return 'Pending Refund';
    };

    const isRefundRequestPending = (status: string): boolean =>
      status === 'Refund Request Pending';

    return (
      <TableContainer component="div">
        <Table aria-label="GCP Transaction List Table">
          <TableHead>
            <TableRow className={classes.headerRow}>
              {tableHeader.map((cell) => (
                <TableCell
                  key={cell.title}
                  align="left"
                  className={classes.headerCell}
                  width="12.5%"
                >
                  <GcpBox ml={2}>
                    <GcpTypography
                      paint="mineShaft"
                      variant="body1"
                      whiteSpace="pre-line"
                    >
                      {t(cell.title)}
                    </GcpTypography>
                  </GcpBox>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item) => (
              <TableRow
                key={item.transactionId}
                hover
                className={classes.bodyRow}
                onClick={() => handleOnClickRow(item)}
              >
                <TableCell className={classes.bodyCell} width="20%">
                  <GcpBox alignItems="center" display="flex" gap={1}>
                    <GcpStatus
                      size={8}
                      status={
                        isRefundRequestPending(item.child.status)
                          ? TransactionStatus.RefundRequestPending
                          : (item.status as TransactionStatus)
                      }
                    />
                    <GcpTypography paint="mineShaft" variant="body1">
                      {isRefundRequestPending(item.child.status)
                        ? t(
                            `transaction_list.status.${TransactionStatus.RefundRequestPending}`
                          )
                        : t(`transaction_list.status.${item.status}`)}
                    </GcpTypography>
                  </GcpBox>
                  {item.child.type &&
                    !isRefundRequestPending(item.child.status) && (
                      <GcpBox className={classes.alert} textAlign="center">
                        <GcpTypography
                          paint="torchRed"
                          transform="capitalize"
                          variant="caption"
                        >
                          {getChildLabel(item.child.type, item.child.status)}
                        </GcpTypography>
                      </GcpBox>
                    )}
                </TableCell>
                <TableCell className={classes.bodyCell} width="18%">
                  <GcpTypography paint="mineShaft" variant="body1">
                    {getDatetimeText(item.createdAt, 'default')}
                  </GcpTypography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <GcpTypography paint="mineShaft" variant="body1">
                    {item.transactionId}
                  </GcpTypography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <GcpTypography paint="mineShaft" variant="body1">
                    {item.tid}
                  </GcpTypography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <GcpTypography paint="mineShaft" variant="body1">
                    {item.payerName}
                  </GcpTypography>
                </TableCell>
                <TableCell className={classes.bodyCell} width="20%">
                  <GcpCurrencyFormat
                    paint="mineShaft"
                    value={item.amount}
                    valueVariant="body1"
                  />
                  <GcpTypography
                    paint="mineShaft"
                    transform="capitalize"
                    variant="body1"
                  >
                    {item.sourceOfFund}
                  </GcpTypography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <GcpTypography paint="mineShaft" variant="body1">
                    {item.ref1}
                  </GcpTypography>
                </TableCell>
                <TableCell className={classes.bodyCell} width="12%">
                  <GcpTypography paint="mineShaft" variant="body1">
                    {item.qrType
                      ? t(
                          `transaction_list.qr_types.${
                            qrCodeTypesLabelMap[item.qrType]
                          }`
                        )
                      : 'N/A'}
                  </GcpTypography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && (
          <GcpBox
            isFullWidth
            alignItems="center"
            display="flex"
            flexDirection="column"
            mt={8}
          >
            <EmptyIcon className={classes.empty} />
            <GcpTypography
              paint="hitGray"
              textAlign="center"
              variant="body1"
              whiteSpace="pre-line"
            >
              {t('transaction_list.empty')}
            </GcpTypography>
          </GcpBox>
        )}
        {data.length !== 0 && (
          <GcpBox display="flex" justifyContent="flex-end" mb={4} mt={4}>
            <GcpBox alignItems="center" display="flex" mr={2.5}>
              <GcpTypography variant="body1">
                {t('transaction_list.viewing', {
                  start: pagination.start + 1,
                  end: pagination.start + pagination.size,
                  total: pagination.total,
                })}
              </GcpTypography>
            </GcpBox>
            <Pagination
              color="primary"
              count={Math.ceil(pagination.total / 20)}
              page={page}
              shape="rounded"
              variant="outlined"
              onChange={(_event, pageNumber) => {
                handleOnChangePage(pageNumber);
              }}
            />
          </GcpBox>
        )}
      </TableContainer>
    );
  }
);

export default GcpTransactionListTable;
